import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { publicApi, setApiRole } from "../api/api";
import { postCreateOrderWithRetry } from "../utils/orderApi";
import CustomFieldRenderer from "../components/CustomFieldRenderer";
import SpinnerOverlay from "../components/SpinnerOverlay";
import ErrorModal from "../components/ErrorModal";
import VideoPlayer from "../components/VideoPlayer";
import ReviewModal from "../components/ReviewModal";
import RecentOrderToast from "../components/RecentOrderToast";
import FAQ from "../components/FAQ";
import useLandingTracking from "../hooks/useLandingTracking";
import getDeviceContext from "../utils/deviceContext";
import { getQueryParam, setCookie, areCookiesEnabled } from "../utils/fbclid";
import { trackLandingPageVisit, trackLeadEvent } from "../utils/metaPageTracking";
import { useCheckoutConfig } from "../checkout";
import { getLandingImages, LANDING_IMAGE_TAG } from "../utils/landingImages";
import { FAKE_WHATSAPP_NUMBERS } from "../config/constants";
import CriticalErrorHandler from "../utils/criticalErrorHandler";
import thumbnail from "../assets/images/astroshubh-thumbnail.jpg";
import limitedoffer from "../assets/images/limited-offer.gif";
import styles from "../styles/LandingPage.module.css";

const NUMEROLOGY_FAQS = [
  {
    question: "How will I receive my report?",
    answer:
      "Your report will be delivered directly to your WhatsApp number within 24-72 hours of order confirmation, as a detailed PDF you can download and keep.",
  },
  {
    question: "Is my personal information secure?",
    answer:
      "Yes. Your details are encrypted and stored securely, never shared with third parties, and used only to prepare your report.",
  },
  {
    question: "How accurate is numerology?",
    answer:
      "Our reports follow the Chaldean numerology system with careful, code-calculated numbers — not guesswork. Numerology is a guiding science; outcomes still depend on your own effort.",
  },
  {
    question: "Can I order for someone else?",
    answer:
      "Yes — just enter their details when placing the order. The report is delivered to the WhatsApp number you provide at checkout.",
  },
];

/**
 * Landing page for the Numerology SERVICE — one page reused by all numerology products
 * (Naam Sudhar now; mobile-number/baby-name/business-name later), config-driven off
 * `u_products.user_input_fields` same as the astrology landing page.
 *
 * Mirrors LandingPage.jsx in full (trust banner, image slider, labels, price, description,
 * video, reviews, FAQ, footer, recent-orders toast) — the only deliberate difference is the
 * form: numerology's fields are fully dynamic per product (CustomFieldRenderer), so there's no
 * fixed birth-profile/addon/consultation block here, since those are astrology-specific
 * concepts that don't apply to any numerology product.
 */
export default function NumerologyLandingPage() {
  const { sku } = useParams();
  const navigate = useNavigate();
  const { checkoutMode } = useCheckoutConfig();
  const visitorId = useLandingTracking(sku);

  const [product, setProduct] = useState(null);
  const [productError, setProductError] = useState("");
  const [productReviews, setProductReviews] = useState([]);
  const [productLabels, setProductLabels] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const [recentOrders, setRecentOrders] = useState([]);
  const [currentToastIndex, setCurrentToastIndex] = useState(0);
  const [showToast, setShowToast] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [errorModal, setErrorModal] = useState({ show: false, message: "", title: "Error" });

  const [mobile, setMobile] = useState("");
  const [language, setLanguage] = useState("Hindi");
  const [formError, setFormError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [termsError, setTermsError] = useState("");
  const [customFields, setCustomFields] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // Resubmit dedup — same pattern as LandingPage.jsx: avoid creating a new lead/order every
  // time the user hits submit again (double-click, or back-button from a failed payment).
  const [existingLead, setExistingLead] = useState(null);
  const [existingOrder, setExistingOrder] = useState(null);

  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const mouseDownX = useRef(null);
  const mouseUpX = useRef(null);
  const whatsappRef = useRef(null);
  const isSubmittingRef = useRef(false);

  // Initialise Meta Pixel and capture ?fbclid= attribution — identical to LandingPage.jsx
  useEffect(() => {
    const pixelId = "780498105149088";
    trackLandingPageVisit(pixelId);

    const fbclid = getQueryParam("fbclid");
    const utm_source = getQueryParam("utm_source");

    if (fbclid) {
      localStorage.setItem("fbclid", fbclid);

      // _fbc cookie in the format Meta expects: fb.1.<timestamp>.<fbclid>
      const ts = Math.floor(Date.now() / 1000);
      const fbc = `fb.1.${ts}.${fbclid}`;

      const cookieSet = setCookie("_fbc", fbc);
      if (!cookieSet) {
        localStorage.setItem("_fbc_fallback", fbc);
        console.log(
          "Cookies disabled - storing Facebook tracking data in localStorage"
        );
      }
    }

    if (utm_source === "facebook") {
      localStorage.setItem("utm_source", "facebook");
    }

    if (!areCookiesEnabled()) {
      console.warn(
        "Cookies are disabled in this browser. Facebook pixel tracking may be limited."
      );
    }
  }, []);

  // Product + reviews + labels — same shape as LandingPage.jsx's product-load effect.
  useEffect(() => {
    publicApi
      .get(`/products/${sku}`, { params: { image_tag: LANDING_IMAGE_TAG } })
      .then((res) => {
        setProduct(res.data.data);
        const { images: landingImgs, mainIndex: mainIdx } = getLandingImages(res.data.data);
        setCurrentImage(mainIdx);

        // Preload the LCP image as soon as the URL is known (same as LandingPage.jsx)
        const lcpImageUrl =
          landingImgs.length > 0 ? landingImgs[mainIdx] : res.data.data?.image;
        if (lcpImageUrl && typeof document !== "undefined") {
          const existing = document.querySelector(`link[rel="preload"][href="${lcpImageUrl}"]`);
          if (!existing) {
            const preload = document.createElement("link");
            preload.rel = "preload";
            preload.as = "image";
            preload.href = lcpImageUrl;
            preload.fetchPriority = "high";
            document.head.appendChild(preload);
          }
        }
        if (res.data.data?.id) {
          publicApi
            .get(`/product-reviews/${res.data.data.id}`)
            .then((r) => r.data.status === "success" && setProductReviews(r.data.data || []))
            .catch(() => {});
          publicApi
            .get(`/products/${res.data.data.id}/labels`)
            .then((r) => r.data.success && setProductLabels(r.data.data || []))
            .catch(() => {});
        }
      })
      .catch((err) => {
        console.error("Error loading numerology product:", err);
        CriticalErrorHandler.reportApiError(`/products/${sku}`, err);
        setProductError("Please refresh the page or try again after some time.");
      });
  }, [sku]);

  // Recent orders toast — same as LandingPage.jsx.
  useEffect(() => {
    publicApi
      .get("/recent-orders")
      .then((res) => {
        if (res.data.status === "success" && res.data.data.length > 0) {
          setRecentOrders(res.data.data);
          setTimeout(() => {
            setShowToast(true);
            setCurrentToastIndex(0);
          }, 1500);
        }
      })
      .catch(() => {});
  }, []);

  const handleToastClose = () => {
    setShowToast(false);
    if (currentToastIndex + 1 < recentOrders.length) {
      setTimeout(() => {
        setCurrentToastIndex((prev) => prev + 1);
        setShowToast(true);
      }, 3000);
    }
  };

  const landingImages = product ? getLandingImages(product).images : [];
  const images = landingImages.length > 0 ? landingImages : [];

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove = (e) => { touchEndX.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const delta = touchEndX.current - touchStartX.current;
      if (Math.abs(delta) > 40) {
        if (delta < 0) setCurrentImage((prev) => (prev + 1) % images.length);
        else setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };
  const handleMouseDown = (e) => { mouseDownX.current = e.clientX; };
  const handleMouseUp = (e) => {
    mouseUpX.current = e.clientX;
    if (mouseDownX.current !== null && mouseUpX.current !== null) {
      const delta = mouseUpX.current - mouseDownX.current;
      if (Math.abs(delta) > 40) {
        if (delta < 0) setCurrentImage((prev) => (prev + 1) % images.length);
        else setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
      }
    }
    mouseDownX.current = null;
    mouseUpX.current = null;
  };

  // Same scroll+focus behavior as LandingPage.jsx's handleOrderNowClick, now targeting the
  // WhatsApp number field (the first fixed field) since Full Name was removed.
  const handleOrderNowClick = () => {
    if (whatsappRef.current) {
      whatsappRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => whatsappRef.current.focus(), 400);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setCustomFields((prev) => ({ ...prev, [fieldId]: value }));
    setFieldErrors((prev) => ({ ...prev, [fieldId]: undefined }));
  };

  // Numerology products have no standalone "name" field — the customer's name comes from
  // whichever dynamic custom field is keyed "firstName" (the same key numerologyUtils.js
  // requires server-side), not a fixed form input.
  const getFirstName = () => {
    const field = (product?.user_input_fields || []).find((f) => f.key === "firstName");
    const value = field ? customFields[field.id] : undefined;
    return typeof value === "string" ? value.trim() : "";
  };

  const validateFields = () => {
    const errors = {};
    (product?.user_input_fields || []).forEach((field) => {
      const value = customFields[field.id];
      const isEmpty =
        field.type === "checkbox"
          ? value === undefined
          : field.type === "array"
          ? !Array.isArray(value) || value.length === 0
          : value === undefined || value === null || value === "";
      if (field.required && isEmpty) {
        errors[field.id] = `${field.label} is required`;
        return;
      }
      // Belt-and-braces: DateOfBirthField already blocks these at entry time, but a
      // product's field config (notFuture/minYear) may change after a value was
      // already selected, so re-check against the current config on submit too —
      // mirrors backend-app/utils/customFieldValidator.js's date case.
      if (!isEmpty && field.type === "date") {
        if (field.notFuture) {
          const todayStr = new Date().toISOString().slice(0, 10);
          if (value > todayStr) {
            errors[field.id] = `${field.label} cannot be in the future`;
            return;
          }
        }
        if (field.minYear) {
          const year = parseInt(String(value).slice(0, 4), 10);
          if (!isNaN(year) && year < field.minYear) {
            errors[field.id] = `${field.label} year must be ${field.minYear} or later`;
          }
        }
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const transformCustomFields = () => {
    const transformed = {};
    (product?.user_input_fields || []).forEach((field) => {
      const value = customFields[field.id];
      if (value !== undefined && value !== null && value !== "") {
        // `key` is the stable identifier the numerology engine looks values up by (e.g.
        // "firstName", "concern") — `label` is just the editable display text an admin can
        // reword anytime, so storing only {label, value} let a relabeled field silently stop
        // matching what the engine expected. Storing key alongside keeps this order's data
        // self-describing even if the product's fields change later.
        transformed[field.id] = { label: field.label, key: field.key, value };
      }
    });
    return Object.keys(transformed).length > 0 ? transformed : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setErrorModal({ show: false, message: "", title: "Error" });
    setFormError("");

    const whatsappDigits = mobile.replace(/\D/g, "").slice(-10);
    if (!mobile || !/^\d{10}$/.test(whatsappDigits)) {
      setErrorModal({ show: true, message: "Enter a valid 10-digit WhatsApp number.", title: "Invalid Number" });
      isSubmittingRef.current = false;
      return;
    }
    // Same fake/test-number screen as LandingPage.jsx — keeps obviously fake numbers out of
    // the payment flow (and out of WhatsApp delivery, which would just fail silently later).
    if (
      FAKE_WHATSAPP_NUMBERS.includes(whatsappDigits) ||
      /^(\d)\1{9}$/.test(whatsappDigits) ||
      /^123\d{7}$/.test(whatsappDigits) ||
      /^12\d{8}$/.test(whatsappDigits)
    ) {
      setErrorModal({ show: true, message: "Please enter a valid WhatsApp number", title: "Invalid Number" });
      isSubmittingRef.current = false;
      return;
    }
    if (!termsAccepted) {
      setTermsError("Please accept the terms to continue.");
      isSubmittingRef.current = false;
      return;
    }
    setTermsError("");
    if (!validateFields()) {
      isSubmittingRef.current = false;
      return;
    }

    setLoading(true);
    setLoadingMessage("Preparing your order...");
    const deviceContext = getDeviceContext();
    const whatsapp = mobile.replace(/\D/g, "").slice(-10);
    const firstName = getFirstName();

    try {
      const orderItems = [
        {
          item_id: product.id,
          item_name: product.name,
          item_price: parseFloat(product.price),
          item_type: "main",
          service_id: product.service_id,
          service_name: product.service || product.service_name || "Numerology",
          custom_fields: transformCustomFields(),
        },
      ];
      const orderSnapshot = {
        order_items: orderItems,
        total_amount: product.price,
        snapshot_date: new Date().toISOString(),
      };

      // Resubmit dedup — same pattern as LandingPage.jsx: a prior submit on this page
      // already produced a lead/order; reuse it unchanged, update it if the form changed,
      // and only hit /create-lead for a genuinely first submit.
      const currentFormSnapshot = JSON.stringify({
        language,
        customFields,
        orderSnapshot,
      });

      if (existingLead && existingOrder) {
        if (currentFormSnapshot === existingOrder.formSnapshot) {
          navigate("/order-payment", {
            state: {
              orderId: existingOrder.orderId,
              userId: existingOrder.userId,
              amount: existingOrder.amt,
              leadId: existingLead.leadId,
              customerName: firstName,
              customerContact: whatsapp,
              product: { id: product.id, service_name: product.service_name || product.service },
              checkoutMode,
            },
          });
          return;
        }

        setLoadingMessage("Updating your order...");
        const updateRes = await postCreateOrderWithRetry({
          mobile: whatsapp,
          profile: { full_name: firstName },
          order_id: existingOrder.orderId,
          lead_id: existingLead.leadId,
          product_id: product.id,
          service_id: product.service_id,
          report_prefered_lang: language || "Hindi",
          amount: product.price,
          remark: "",
          selected_addons: orderSnapshot,
          is_profile_required: false,
          skip_otp: true,
          deviceContext,
        });

        if (updateRes.data.status !== "success") {
          throw new Error(updateRes.data.message || "Failed to update order.");
        }

        const { order } = updateRes.data.data;
        setExistingOrder({ ...order, formSnapshot: currentFormSnapshot });

        navigate("/order-payment", {
          state: {
            orderId: order.orderId,
            userId: order.userId,
            amount: order.amt,
            leadId: existingLead.leadId,
            customerName: firstName,
            customerContact: whatsapp,
            product: { id: product.id, service_name: product.service_name || product.service },
            checkoutMode,
          },
        });
        return;
      }

      const leadRes = await publicApi.post("/create-lead", {
        mobile: whatsapp,
        profile: { full_name: firstName },
        product_id: product.id,
        service_id: product.service_id,
        amount: product.price,
        report_prefered_lang: language || "Hindi",
        remark: "",
        selected_addons: orderSnapshot,
        visitor_id: visitorId,
        utm_source: getQueryParam("utm_source"),
      });

      const leadId = leadRes.data.data.leadId;
      setExistingLead({ leadId });
      trackLeadEvent(product);

      const orderRes = await postCreateOrderWithRetry({
        mobile: whatsapp,
        profile: { full_name: firstName },
        lead_id: leadId,
        product_id: product.id,
        service_id: product.service_id,
        report_prefered_lang: language || "Hindi",
        amount: product.price,
        remark: "",
        selected_addons: orderSnapshot,
        is_profile_required: false,
        skip_otp: true,
        deviceContext,
        visitor_id: visitorId,
        utm_source: getQueryParam("utm_source"),
      });

      if (orderRes.data.status !== "success") {
        throw new Error(orderRes.data.message || "Failed to create order.");
      }

      const { order, user } = orderRes.data.data;
      setExistingOrder({ ...order, formSnapshot: currentFormSnapshot });
      if (orderRes.data.data.token) {
        localStorage.setItem("userRefreshToken", orderRes.data.data.token.refreshToken);
        localStorage.setItem("userAccessToken", orderRes.data.data.token.accessToken);
        localStorage.setItem("user", JSON.stringify(user));
        // Without this, api.js's interceptor stays on its default "admin" role and never
        // attaches the userAccessToken we just stored — CommonPaymentPage's /wallet/initiate
        // call then goes out unauthenticated and fails with a generic "Payment Error".
        setApiRole("user");
      }

      if (visitorId) {
        publicApi
          .post("/landing/link", { visitor_id: visitorId, lead_id: leadId, order_id: order.orderId })
          .catch(() => {});
      }

      navigate("/order-payment", {
        state: {
          orderId: order.orderId,
          userId: order.userId,
          amount: order.amt,
          leadId,
          customerName: user?.name || "",
          customerContact: whatsapp,
          product: { id: product.id, service_name: product.service_name || product.service },
          checkoutMode,
        },
      });
    } catch (err) {
      console.error("Numerology order creation failed:", err);
      setErrorModal({
        show: true,
        message: err.response?.data?.message || err.message || "Failed to create order.",
        title: "Order Error",
      });
      CriticalErrorHandler.reportFormError("Numerology Order Creation", err, {
        product_id: product?.id,
        mobile: whatsapp,
      });
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  if (productError) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", padding: "20px" }}>
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            padding: "40px",
            textAlign: "center",
            maxWidth: "500px",
            border: "1px solid #f0f0f0",
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              backgroundColor: "#fff3e0",
              margin: "0 auto 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "3px solid #ffcc80",
            }}
          >
            <span style={{ fontSize: "24px", color: "#f57c00" }}>⚠️</span>
          </div>
          <h2 style={{ color: "#3E2723", fontSize: "24px", fontWeight: "600", margin: "0 0 16px 0", fontFamily: "inherit" }}>
            Product currently unavailable.
          </h2>
          <p style={{ color: "#666", fontSize: "16px", lineHeight: "1.5", margin: "0 0 32px 0", fontFamily: "inherit" }}>
            {productError}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: "#f57c00",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "12px 24px",
              fontSize: "16px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              fontFamily: "inherit",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#e65100")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#f57c00")}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  if (!product) {
    return (
      <div style={{ padding: 40, textAlign: "center", fontSize: 22, color: "#888" }}>
        Loading...
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {loading && <SpinnerOverlay message={loadingMessage} />}
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        title={errorModal.title}
        onClose={() => setErrorModal({ show: false, message: "", title: "Error" })}
      />

      {/* Trust Banner */}
      <div className={styles.trustBanner}>
        <span className={styles.trustItem}>
          <span className={styles.trustIcon}>⭐</span> 15+ years experience
        </span>
        <span className={styles.trustSeparator}>|</span>
        <span className={styles.trustItem}>
          <span className={styles.trustIcon}>📦</span> 50000+ reports delivered
        </span>
      </div>

      {/* Header */}
      <div className={styles.header}>
        <img
          src={require("../assets/images/vedic-wisdom-logo.png")}
          alt="Vedic Wisdom Logo"
          className={styles.logo}
        />
      </div>
      {product.tagline && <h1 className={styles.title}>{product.tagline}</h1>}

      <div className={styles.mainSection}>
        <section className={styles.heroBanner}>
          <div className={styles.heroColumns}>
            <div className={styles.card}>
              {product.badge && (
                <div className={styles.productBadge}>
                  <div className={styles.badgeBody}>{product.badge}</div>
                  <div className={styles.badgeArrow}>
                    <div className={styles.badgeArrowBorder}></div>
                  </div>
                </div>
              )}

              {/* Image slider — swipe/drag, same as LandingPage.jsx */}
              <div className={styles.heroImageSlider}>
                <img
                  src={images[currentImage] || "https://via.placeholder.com/320x210?text=No+Image"}
                  alt={product.name || "Product image"}
                  className={styles.productImage}
                  loading="eager"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  draggable={false}
                />
                {images.length > 1 && (
                  <div className={styles.sliderIndicators}>
                    {images.map((_, idx) => (
                      <span
                        key={idx}
                        className={idx === currentImage ? `${styles.indicatorDot} ${styles.activeDot}` : styles.indicatorDot}
                        onClick={() => setCurrentImage(idx)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <h1 style={{ fontSize: "1.3rem", fontWeight: 800, color: "#3E2723", marginBottom: 18, lineHeight: 1.15 }}>
                {product.name}
              </h1>

              {productLabels.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                  {productLabels.map((label, index) => {
                    const colorStyles = {
                      green: { backgroundColor: "#395c3a", color: "white" },
                      blue: { backgroundColor: "#d9dbff", color: "black" },
                      seagreen: { backgroundColor: "#2E8B57", color: "black" },
                      skyblue: { backgroundColor: "#87CEEB", color: "black" },
                      orange: { backgroundColor: "#a8720d", color: "white" },
                      lightorange: { backgroundColor: "#FFDBBB", color: "black" },
                      red: { backgroundColor: "#f77369", color: "black" },
                      purple: { backgroundColor: "#d392df", color: "black" },
                      teal: { backgroundColor: "#8bdad2", color: "black" },
                    };
                    const style = colorStyles[label.label_color] || colorStyles.green;
                    return (
                      <span
                        key={label.id || index}
                        style={{ ...style, padding: "2px 4px", borderRadius: 4, fontSize: 12, fontWeight: 300, display: "inline-block" }}
                      >
                        {label.label_text}
                      </span>
                    );
                  })}
                </div>
              )}

              <div>
                <span className={styles.price}>₹{product.price} </span>
                {product.list_price && <span className={styles.listprice}>₹{product.list_price}</span>}
                <span className={styles.priceNote}>(incl. all taxes)</span>
              </div>

              {/* Mobile Order Now Button */}
              <button type="button" className={styles.mobileOrderBtn} style={{ width: "100%" }} onClick={handleOrderNowClick}>
                Order now your {product.service}
              </button>
              <div className={styles.reviewSection}>
                <div className={styles.stars}>
                  <span className={styles.starFilled}>★</span>
                  <span className={styles.starFilled}>★</span>
                  <span className={styles.starFilled}>★</span>
                  <span className={styles.starFilled}>★</span>
                  <span className={styles.starHalf}>★</span>
                </div>
                <span className={styles.rating}>4.9/5</span>
                <a
                  href="#customer-reviews"
                  className={styles.reviewCount}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("customer-reviews")?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  (8k+ reviews)
                </a>
              </div>
              <div className={styles.trustBadges}>
                <span className={styles.badge}>✅ Verified Service</span>
              </div>
              <ul className={styles.benefits}>
                <li>🔒 100% Privacy. Your data is never shared.</li>
                <li style={{ display: "flex", alignItems: "center" }}>
                  <FaWhatsapp size={18} style={{ color: "#25D366", marginRight: "8px" }} />
                  Delivered via WhatsApp
                </li>
                <li>🛡️ Trusted by 50,000+ users</li>
                <li className={styles.reportLanguages}>
                  📝 Get Reports in
                  <div className={styles.languageContainer}>
                    {/* Numerology reports are generated in every language the Report
                        Language dropdown below offers, so all tags show regardless of
                        product.profile_required (LandingPage.jsx still limits non-profile
                        astrology products to हिंदी/English). */}
                    {["हिंदी", "English", "Marathi", "Gujarati", "Tamil", "Telugu", "Kannada", "Bengali", "Punjabi"].map((lang) => (
                      <span key={lang} className={styles.languageTag}>
                        {lang}
                      </span>
                    ))}
                  </div>
                </li>
              </ul>
            </div>

            {/* Form card — the ONLY part that's numerology-specific: fully dynamic fields */}
            <div className={styles.card}>
              <form autoComplete="off" onSubmit={handleSubmit}>
                <div className={styles.formTitle}>
                  Get Your {product.service || "Numerology"} Report
                  <img src={limitedoffer} alt="limited-time-offer" className={styles.limitedoffer} />
                </div>

                {formError && (
                  <div id="form-error" role="alert" className={styles.formError} style={{ marginBottom: 12 }}>
                    {formError}
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    WhatsApp Number <span style={{ color: "#ff3d00" }}>*</span>
                  </label>
                  <input
                    ref={whatsappRef}
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="10-digit WhatsApp number"
                    maxLength={10}
                    className={styles.formInput}
                    required
                    aria-describedby={formError ? "form-error" : undefined}
                    autoComplete="off"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Report Language/ भाषा </label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className={styles.formSelect}
                  >
                    <option value="Hindi"> हिंदी</option>
                    <option value="English">English</option>
                    <option value="Gujarati">Gujarati</option>
                    <option value="Punjabi">Punjabi</option>
                    <option value="Bengali">Bengali</option>
                    <option value="Telugu">Telugu</option>
                    <option value="Kannada">Kannada</option>
                    <option value="Tamil">Tamil</option>
                    <option value="Marathi">Marathi</option>
                  </select>
                </div>

                {(product.user_input_fields || []).map((field) => (
                  <div key={field.id} className={styles.formGroup}>
                    {field.type !== "checkbox" && (
                      <label className={styles.formLabel}>
                        {field.label} {field.required && <span style={{ color: "#ff3d00" }}>*</span>}
                      </label>
                    )}
                    <CustomFieldRenderer
                      field={field}
                      value={customFields[field.id]}
                      onChange={(value) => handleFieldChange(field.id, value)}
                      inputClassName={styles.formInput}
                      selectClassName={styles.formSelect}
                    />
                    {fieldErrors[field.id] && <div className={styles.formError}>{fieldErrors[field.id]}</div>}
                  </div>
                ))}

                <div className={styles.formGroup}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      background: "#f9f6f2",
                      borderRadius: 8,
                      padding: "12px 16px",
                      border: termsError ? "3px solid #ff3d00" : "2px solid #eee",
                      boxShadow: termsError ? "0 0 0 6px #ffd180" : "0 1px 4px rgba(0,0,0,0.04)",
                      transition: "border 0.2s, box-shadow 0.2s",
                      animation: termsError ? "shake 0.3s" : "none",
                    }}
                  >
                    {termsError && (
                      <style>{`
                        @keyframes shake {
                          0% { transform: translateX(0); }
                          25% { transform: translateX(-4px); }
                          50% { transform: translateX(4px); }
                          75% { transform: translateX(-4px); }
                          100% { transform: translateX(0); }
                        }
                      `}</style>
                    )}
                    <input
                      type="checkbox"
                      id="numerology-terms"
                      checked={termsAccepted}
                      onChange={(e) => {
                        setTermsAccepted(e.target.checked);
                        if (e.target.checked) setTermsError("");
                      }}
                      required
                      aria-required="true"
                      aria-describedby={termsError ? "terms-error" : "terms-label"}
                      style={{ accentColor: "#f57c00", width: 12, height: 12, marginRight: 6 }}
                    />
                    <label
                      htmlFor="numerology-terms"
                      id="terms-label"
                      style={{ fontWeight: 500, fontSize: "0.6rem", color: "#3E2723", margin: 0, cursor: "pointer" }}
                    >
                      I have read and accept the
                      <a
                        href="/terms-and-condition"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#f57c00", textDecoration: "underline", fontWeight: 700, fontSize: "0.6rem", marginLeft: 6 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open("/terms-and-condition", "_blank");
                        }}
                      >
                        Terms &amp; Conditions
                      </a>
                    </label>
                  </div>
                  {termsError && (
                    <div id="terms-error" role="alert" aria-live="assertive" style={{ color: "#f57c00", fontWeight: 600, marginTop: 6, fontSize: 15 }}>
                      {termsError}
                    </div>
                  )}
                </div>

                <button type="submit" className={styles.submitBtn} disabled={loading}>
                  {loading ? "Processing..." : `Get Report @₹${product.price}`}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* Description */}
        {product.description && (
          <section className={styles.descriptionSection}>
            <h2 className={styles.descriptionTitle}>About this item</h2>
            <div className={styles.descriptionText} dangerouslySetInnerHTML={{ __html: product.description }} />
          </section>
        )}

        {/* Video */}
        {product.video_url && (
          <section className={styles.descriptionSection}>
            <h2 className={styles.descriptionTitle}>Connect more</h2>
            <VideoPlayer videoUrl={product.video_url} thumbnail={product.video_thumbnail || thumbnail} />
          </section>
        )}

        {/* Reviews */}
        {productReviews.length > 0 && (
          <section id="customer-reviews" className={styles.reviewsSection}>
            <h2 className={styles.reviewsSectionTitle}>Feedback from our users</h2>
            <div className={styles.reviewsList}>
              {productReviews.slice(0, 3).map((review) => (
                <div key={review.id} className={styles.reviewItem}>
                  <p className={styles.reviewText}>{review.review_text}</p>
                  <div className={styles.reviewFooter}>
                    <div className={styles.userProfileImage}>{review.customer_name.charAt(0).toUpperCase()}</div>
                    <div className={styles.userInfo}>
                      <strong className={styles.customerName}>{review.customer_name}</strong>
                      <div className={styles.reviewStars}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className={star <= review.rating ? styles.starFilled : styles.starEmpty}>
                            ★
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {productReviews.length > 3 && (
              <button className={styles.showMoreBtn} onClick={() => setShowReviewModal(true)}>
                Read More Reviews <span className={styles.arrowRight}>›</span>
              </button>
            )}
          </section>
        )}

        {/* FAQ */}
        <section className={styles.faqSection}>
          <h2 className={styles.faqSectionTitle}>Frequently Asked Questions</h2>
          <FAQ faqs={NUMEROLOGY_FAQS} />
        </section>

        {/* Footer */}
        <footer className={styles.footer}>
          <div className={styles.footerContent}>
            <h3 className={styles.footerTitle}>Contact Us</h3>
            <p className={styles.footerText}>
              Email:{" "}
              <a href="mailto:support@astroshubh.com" className={styles.footerLink}>
                support@astroshubh.com
              </a>
            </p>
          </div>
        </footer>
      </div>

      {showReviewModal && <ReviewModal reviews={productReviews} onClose={() => setShowReviewModal(false)} />}
      {showToast && recentOrders[currentToastIndex] && (
        <RecentOrderToast
          name={recentOrders[currentToastIndex].name}
          secondsAgo={recentOrders[currentToastIndex].secondsAgo}
          onClose={handleToastClose}
        />
      )}
    </div>
  );
}
